import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import type { Platform } from "@koc/shared";
import { apiKocs, apiProjects, apiVideos, type VideoFilters } from "../api.js";
import { Badge, Button, Input, Label, PageHeader, Table, fmtNum, fmtTime } from "../components/ui.js";
import { ProjectPicker } from "../components/ProjectPicker.js";
import { KocPicker } from "../components/KocPicker.js";
import { KeywordPicker } from "../components/KeywordPicker.js";
import { VideoImportDialog } from "./VideoImportDialog.js";

function unixToDateInput(unix: number | undefined): string {
  if (!unix) return "";
  const d = new Date(unix * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dateInputToUnix(value: string, edge: "start" | "end"): number | undefined {
  if (!value) return undefined;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  const date = edge === "start"
    ? new Date(y, m - 1, d, 0, 0, 0, 0)
    : new Date(y, m - 1, d, 23, 59, 59, 999);
  return Math.floor(date.getTime() / 1000);
}

export function VideosPage() {
  const [filters, setFilters] = useState<VideoFilters>({ limit: 100, offset: 0 });
  const [showImport, setShowImport] = useState(false);
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["videos", filters], queryFn: () => apiVideos.list(filters) });

  const projectsQuery = useQuery({
    queryKey: ["projects-all"],
    queryFn: () => apiProjects.list().then((r) => r.items),
  });
  const projects = projectsQuery.data ?? [];

  const kocsQuery = useQuery({
    queryKey: ["kocs-all"],
    queryFn: () => apiKocs.list().then((r) => r.items),
  });
  const kocs = kocsQuery.data ?? [];

  const allKeywords = useMemo(() => {
    const set = new Set<string>();
    for (const p of projects) for (const k of p.monitor_keywords) set.add(k);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [projects]);

  return (
    <>
      <PageHeader
        title="Videos"
        actions={
          <>
            <Button onClick={() => setShowImport(true)}>+ Add videos</Button>
            <a href={apiVideos.exportCsvUrl(filters)} download>
              <Button variant="secondary">Export CSV</Button>
            </a>
          </>
        }
      />
      {showImport && <VideoImportDialog onClose={() => setShowImport(false)} />}
      <div className="grid grid-cols-4 gap-3 mb-3">
        <div>
          <Label>Title search</Label>
          <Input
            value={filters.title ?? ""}
            onChange={(e) => setFilters({ ...filters, title: e.target.value || undefined, offset: 0 })}
            placeholder="search by title…"
          />
        </div>
        <div>
          <Label>Project</Label>
          <ProjectPicker
            projects={projects}
            value={filters.projectId ?? null}
            onChange={(projectId) => setFilters({ ...filters, projectId: projectId ?? undefined, offset: 0 })}
          />
        </div>
        <div>
          <Label>KOC</Label>
          <KocPicker
            kocs={kocs}
            value={filters.kocId ?? null}
            onChange={(kocId) => setFilters({ ...filters, kocId: kocId ?? undefined, offset: 0 })}
          />
        </div>
        <div>
          <Label>Keyword</Label>
          <KeywordPicker
            keywords={allKeywords}
            value={filters.keyword ?? null}
            onChange={(keyword) => setFilters({ ...filters, keyword: keyword ?? undefined, offset: 0 })}
          />
        </div>
        <div>
          <Label>Platform</Label>
          <select
            className="block w-full rounded border border-stone-300 px-3 py-1.5 text-sm bg-white"
            value={filters.platform ?? ""}
            onChange={(e) => setFilters({ ...filters, platform: (e.target.value || undefined) as Platform | undefined, offset: 0 })}
          >
            <option value="">All</option>
            <option value="tiktok">TikTok</option>
            <option value="instagram">Instagram</option>
            <option value="youtube">YouTube</option>
          </select>
        </div>
        <div>
          <Label>Hit only</Label>
          <select
            className="block w-full rounded border border-stone-300 px-3 py-1.5 text-sm bg-white"
            value={filters.hasHit ?? ""}
            onChange={(e) => setFilters({ ...filters, hasHit: (e.target.value || undefined) as "1" | "0" | undefined, offset: 0 })}
          >
            <option value="">All</option>
            <option value="1">Hits only</option>
            <option value="0">No hits</option>
          </select>
        </div>
        <div>
          <Label>Published from</Label>
          <Input
            type="date"
            value={unixToDateInput(filters.publishedFrom)}
            onChange={(e) => setFilters({ ...filters, publishedFrom: dateInputToUnix(e.target.value, "start"), offset: 0 })}
          />
        </div>
        <div>
          <Label>Published to</Label>
          <Input
            type="date"
            value={unixToDateInput(filters.publishedTo)}
            onChange={(e) => setFilters({ ...filters, publishedTo: dateInputToUnix(e.target.value, "end"), offset: 0 })}
          />
        </div>
        <div className="col-span-4 flex justify-end">
          <Button variant="secondary" onClick={() => setFilters({ limit: 100, offset: 0 })}>
            Clear filters
          </Button>
        </div>
      </div>
      <Table
        rows={q.data?.items ?? []}
        onRowClick={(r) => navigate({ to: "/videos/$videoId", params: { videoId: String(r.id) } })}
        columns={[
          { header: "Platform", cell: (r) => <Badge>{r.platform}</Badge>, className: "w-20" },
          { header: "Title", cell: (r) => (
            <Link to="/videos/$videoId" params={{ videoId: String(r.id) }} className="hover:underline">
              {r.title || <span className="text-stone-400">(no title)</span>}
            </Link>
          )},
          { header: "Project", cell: (r) => (
            <Link to="/projects/$projectId" params={{ projectId: String(r.project_id) }} className="text-xs hover:underline">
              {r.project_name}
            </Link>
          ) },
          { header: "KOC", cell: (r) => r.koc_id != null
            ? <Link to="/kocs/$kocId" params={{ kocId: String(r.koc_id) }} className="text-xs hover:underline">{r.koc_name ?? "—"}</Link>
            : <span className="text-xs text-stone-400">—</span> },
          { header: "Hit", cell: (r) => r.has_keyword_hit ? (
            <span title={r.keyword_hits.join(", ")}><Badge tone="green">hit</Badge></span>
          ) : null, className: "w-16" },
          { header: "Views", cell: (r) => fmtNum(r.latest_view_count), className: "text-right tabular-nums w-24" },
          { header: "Likes", cell: (r) => fmtNum(r.latest_like_count), className: "text-right tabular-nums w-24" },
          { header: "Comments", cell: (r) => fmtNum(r.latest_comment_count), className: "text-right tabular-nums w-24" },
          { header: "Published", cell: (r) => <span className="text-xs text-stone-500">{fmtTime(r.publish_time)}</span> },
        ]}
      />
      <div className="mt-3 flex justify-between items-center text-sm text-stone-500">
        <div>{q.data?.items.length ?? 0} rows</div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            disabled={(filters.offset ?? 0) === 0}
            onClick={() => setFilters({ ...filters, offset: Math.max(0, (filters.offset ?? 0) - (filters.limit ?? 100)) })}
          >
            ← Prev
          </Button>
          <Button
            variant="ghost"
            disabled={(q.data?.items.length ?? 0) < (filters.limit ?? 100)}
            onClick={() => setFilters({ ...filters, offset: (filters.offset ?? 0) + (filters.limit ?? 100) })}
          >
            Next →
          </Button>
        </div>
      </div>
    </>
  );
}
